/** Reusable item search box backed by the cached Torn catalog. */

import { catalog$, searchItems } from "../data/catalog.ts";
import type { CatalogItem } from "../state/types.ts";
import { itemImage } from "./links.ts";
import { money } from "../util/format.ts";
import { append, clear, el, on } from "./dom.ts";

export interface TypeaheadOptions {
  placeholder: string;
  onPick: (item: CatalogItem) => void;
  filter?: (item: CatalogItem) => boolean;
  /** When set, the input is disabled and this explains why. */
  disabledReason?: () => string | null;
}

export interface TypeaheadHandle {
  root: HTMLElement;
  refresh: () => void;
  clearInput: () => void;
}

export function createTypeahead(options: TypeaheadOptions): TypeaheadHandle {
  const input = el("input", {
    type: "search",
    class: "form-control",
    placeholder: options.placeholder,
    autocomplete: "off",
    spellcheck: "false",
    role: "combobox",
    "aria-expanded": "false",
    "aria-autocomplete": "list",
  });

  const menu = el("div", { class: "typeahead-menu d-none", role: "listbox" });
  const root = el("div", { class: "typeahead" }, input, menu);

  let results: CatalogItem[] = [];
  let activeIndex = -1;

  const closeMenu = () => {
    menu.classList.add("d-none");
    input.setAttribute("aria-expanded", "false");
    activeIndex = -1;
  };

  const pick = (item: CatalogItem) => {
    options.onPick(item);
    input.value = "";
    results = [];
    closeMenu();
  };

  const render = () => {
    clear(menu);

    if (results.length === 0) {
      const query = input.value.trim();
      if (query.length === 0) {
        closeMenu();
        return;
      }
      append(
        menu,
        el("div", {
          class: "px-3 py-2 text-body-secondary tiny",
          text: catalog$.get().items.length === 0 ? "Item catalog not loaded yet." : "No matches.",
        }),
      );
      menu.classList.remove("d-none");
      input.setAttribute("aria-expanded", "true");
      return;
    }

    results.forEach((item, index) => {
      const option = el(
        "button",
        {
          type: "button",
          class: `typeahead-option${index === activeIndex ? " active" : ""}`,
          role: "option",
          "aria-selected": index === activeIndex ? "true" : "false",
        },
        el("img", {
          src: itemImage(item.id),
          alt: "",
          class: "item-thumb",
          loading: "lazy",
          width: 46,
          height: 26,
        }),
        el(
          "span",
          { class: "flex-grow-1 min-w-0" },
          el("span", { class: "d-block text-truncate", text: item.name }),
          el("span", { class: "d-block tiny text-body-secondary", text: item.type }),
        ),
        el("span", { class: "mono tiny text-body-secondary", text: money(item.marketValue) }),
      );

      // mousedown, not click: blur would close the menu before click landed.
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
        pick(item);
      });

      append(menu, option);
    });

    menu.classList.remove("d-none");
    input.setAttribute("aria-expanded", "true");
  };

  const search = () => {
    const query = input.value.trim();
    results = query.length === 0 ? [] : searchItems(query, 8, options.filter);
    activeIndex = results.length > 0 ? 0 : -1;
    render();
  };

  on(input, "input", search);
  on(input, "focus", () => {
    if (input.value.trim().length > 0) search();
  });
  on(input, "blur", () => window.setTimeout(closeMenu, 120));

  on(input, "keydown", (event) => {
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      activeIndex = (activeIndex + 1) % results.length;
      render();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      activeIndex = (activeIndex - 1 + results.length) % results.length;
      render();
    } else if (event.key === "Enter") {
      const chosen = results[activeIndex];
      if (chosen) {
        event.preventDefault();
        pick(chosen);
      }
    } else if (event.key === "Escape") {
      closeMenu();
    }
  });

  const refresh = () => {
    const reason = options.disabledReason?.() ?? null;
    input.disabled = reason !== null;
    input.placeholder = reason ?? options.placeholder;
    if (reason !== null) closeMenu();
  };

  refresh();

  return {
    root,
    refresh,
    clearInput: () => {
      input.value = "";
      closeMenu();
    },
  };
}
